/**
 * Library state shared by the list pane and the detail pane.
 *
 * Module-level, like identity.ts: the list and the open link are on screen
 * together, so starring or retagging in the detail pane has to show in the
 * list row (and the tag counts) without a refetch of either.
 */

import type { LinkDetail, LinkSummary, Stats, TagCount } from '@satisa-memory/shared'
import { ref } from 'vue'
import { api, unwrap } from './api'
import { ownOf } from './format'
import { forgetLink, keepLink } from './link-cache'
import { parseQuery } from './query'

export interface Filters {
  /** The raw search box: free text plus #tag tokens. */
  q: string
  source: string
  person: string
  starred: boolean
  archived: boolean
  /** The Trash instead of the library. */
  trashed: boolean
}

const items = ref<LinkSummary[]>([])
const nextCursor = ref<string | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const tags = ref<TagCount[]>([])
const stats = ref<Stats | null>(null)

let generation = 0
/** The filters of the list on screen, for `refresh`. */
let shown: Filters | null = null

function toQuery(f: Filters, offset?: string) {
  const { text, tags } = parseQuery(f.q)
  return {
    q: text || undefined,
    tag: tags.length ? tags.join(',') : undefined,
    // One of the memory's own kinds stands in for its source (format.ts).
    ...(ownOf(f.source) ? { own: ownOf(f.source)! } : { source: f.source || undefined }),
    person: f.person || undefined,
    starred: f.starred ? '1' : undefined,
    archived: f.archived ? '1' : undefined,
    trashed: f.trashed ? '1' : undefined,
    offset
  }
}

async function load(f: Filters, append = false) {
  // A slow response for an older query must not overwrite a newer one.
  const mine = ++generation
  if (!append) shown = { ...f }
  loading.value = true
  error.value = null
  try {
    const res = await unwrap(
      api.api.links.$get({
        query: toQuery(f, append ? (nextCursor.value ?? undefined) : undefined)
      })
    )
    if (mine !== generation) return
    items.value = append ? [...items.value, ...res.items] : res.items
    nextCursor.value = res.nextCursor
  } catch (e) {
    if (mine !== generation) return
    error.value = e instanceof Error ? e.message : 'Failed to load'
  } finally {
    if (mine === generation) loading.value = false
  }
}

async function loadFacets() {
  try {
    ;[tags.value, stats.value] = await Promise.all([
      unwrap(api.api.tags.$get()),
      unwrap(api.api.stats.$get())
    ])
  } catch {
    /* facets are a convenience; the list reports its own errors */
  }
}

/**
 * Fold an edited link back into the list row it came from, and refresh the
 * facet counts (tags, starred, archived) when the edit moved any of them.
 */
function applyDetail(before: LinkDetail | null, link: LinkDetail) {
  const i = items.value.findIndex((l) => l.id === link.id)
  if (i >= 0) {
    const { id, url, source, externalId, title, kind, summary, tags, starred, archived } = link
    items.value[i] = {
      ...items.value[i]!,
      ...{ id, url, source, externalId, title, kind, summary, tags, starred, archived }
    }
  }
  const moved =
    !before ||
    before.starred !== link.starred ||
    before.archived !== link.archived ||
    before.trashedAt !== link.trashedAt ||
    before.tags.join(',') !== link.tags.join(',')
  if (moved) void loadFacets()
}

/** Take a link out of the list on screen: trashed, restored or deleted for good. */
function dropLink(id: string) {
  items.value = items.value.filter((l) => l.id !== id)
  void loadFacets()
}

/**
 * Archive, trash or restore a link from its row (a swipe). The library lists
 * either state, never both, so the row leaves the list; the undo returned
 * puts the link back as it was, in the row it had while that list is still
 * the one on screen.
 */
async function moveLink(
  id: string,
  body: { archived: boolean } | { trashed: boolean }
): Promise<() => Promise<void>> {
  const at = items.value.findIndex((l) => l.id === id)
  const row = items.value[at]
  const list = JSON.stringify(shown)
  keepLink(await unwrap(api.api.links[':id'].$patch({ param: { id }, json: body })))
  dropLink(id)
  return async () => {
    const back = 'archived' in body ? { archived: !body.archived } : { trashed: !body.trashed }
    keepLink(await unwrap(api.api.links[':id'].$patch({ param: { id }, json: back })))
    if (row && JSON.stringify(shown) === list && !items.value.some((l) => l.id === id)) {
      const next = [...items.value]
      next.splice(Math.min(at, next.length), 0, row)
      items.value = next
    }
    void loadFacets()
  }
}

/** Delete a link for good from its row in the Trash. */
async function deleteLink(id: string) {
  await unwrap(api.api.links[':id'].$delete({ param: { id } }))
  forgetLink(id)
  dropLink(id)
}

/**
 * Whether the list on screen is already the one for these filters, rows and
 * all: Back to the library keeps it (every page scrolled into), so the
 * scroll offset it had still has its rows to land on.
 */
function holds(f: Filters): boolean {
  return !!shown && items.value.length > 0 && JSON.stringify(shown) === JSON.stringify(f)
}

/**
 * Read the list again from its first page, with the filters it was loaded
 * with, and the facets: something was added elsewhere (a to-do from the
 * quick add) that may belong at its top.
 */
async function refresh() {
  if (!shown) return
  await Promise.all([load(shown), loadFacets()])
}

export function useLibrary() {
  return {
    items,
    nextCursor,
    loading,
    error,
    tags,
    stats,
    load,
    loadFacets,
    holds,
    refresh,
    applyDetail,
    dropLink,
    moveLink,
    deleteLink
  }
}
